/**
 * Local state shape the reducer maintains from `OutMsg` frames.
 *
 * Not a 1:1 mirror of `GameStateSnapshot` — adds client-only concerns (scene
 * name, last message timestamp, scheduled reveals, connection status). Scene
 * rendering is downstream and reads slices of this state via `Store.subscribe`.
 *
 * @packageDocumentation
 */

import type {
  CameraPosition,
  Clue,
  CluePreview,
  LbRow,
  PollOption,
  ScoreDelta,
  ScorerRow,
} from '@sg/shared';

/** Discriminator applied to `<div data-scene>` + `window.__sj_scene`. */
export type SceneName =
  | 'connecting'
  | 'lobby'
  | 'calibration'
  | 'board'
  | 'poll_category'
  | 'poll_clue_value'
  | 'clue_zoom'
  | 'grace'
  | 'answer_reveal'
  | 'round_transition'
  | 'podium';

export interface PollState {
  phase: 'category' | 'clue_value';
  options: PollOption[];
  endsAtMs: number;
  tallies: Record<string, number>;
  winner: string | null;
}

export interface ActiveClue {
  clue: Clue;
  revealAtMs: number;
  hardEndMs: number;
  graceEndMs: number;
}

export interface ArmedClue {
  preview: CluePreview;
  revealAtMs: number;
}

export interface AnswerRevealState {
  displayAnswer: string;
  topScorers: ScorerRow[];
}

export interface ScoreUpdateState {
  deltas: ScoreDelta[];
  leaderboard: LbRow[];
}

export interface CalibrationState {
  revealSentTs: number;
  measuredDelayMs: number | null;
  samples: number;
}

/** Root state object held by {@link Store}. Updated only by the reducer. */
export interface GameState {
  scene: SceneName;
  round: 'r1' | 'r2' | 'final' | 'results' | 'lobby';
  broadcastDelaySeconds: number;
  tenantId: string;
  connection: 'idle' | 'connecting' | 'open' | 'closed' | 'error';
  /** v1.3: per-tenant camera reservation (driven by state_snapshot.settings + URL override). */
  cameraPosition: CameraPosition;
  poll: PollState | null;
  armedClue: ArmedClue | null;
  activeClue: ActiveClue | null;
  answerReveal: AnswerRevealState | null;
  scoreUpdate: ScoreUpdateState | null;
  leaderboard: LbRow[];
  /**
   * Full all-time leaderboard (up to 200) for the lobby panels. Optional on
   * the wire; defaults to `[]` client-side when the field is absent so lobby
   * renders cleanly even against older backends.
   */
  leaderboardAllTime: LbRow[];
  calibration: CalibrationState | null;
  lastServerTs: number;
}

/** Zero state used when the page first boots (before any WS traffic). */
export const INITIAL_STATE: GameState = {
  scene: 'connecting',
  round: 'lobby',
  broadcastDelaySeconds: 5,
  tenantId: '',
  connection: 'idle',
  cameraPosition: 'top-right',
  poll: null,
  armedClue: null,
  activeClue: null,
  answerReveal: null,
  scoreUpdate: null,
  leaderboard: [],
  leaderboardAllTime: [],
  calibration: null,
  lastServerTs: 0,
};
